import React, { useState, useEffect, useCallback } from "react";
import { API } from "../hooks/useApi";

const s = {
  card:    { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "8px", padding: "16px", marginBottom: "10px" },
  title:   { fontSize: "13px", fontWeight: 700, color: "#60a5fa", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" },
  section: { fontSize: "11px", fontWeight: 600, color: "#475569", marginBottom: "8px", marginTop: "12px", textTransform: "uppercase", letterSpacing: "0.5px" },
  row:     { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #0f172a" },
  label:   { fontSize: "12px", color: "#94a3b8" },
  val:     { fontSize: "12px", fontWeight: 600, color: "#e2e8f0" },
  badge:   (ok) => ({
    padding: "2px 7px", borderRadius: "4px", fontSize: "10px", fontWeight: 700,
    background: ok ? "#052e16" : "#1a0a0a",
    color:      ok ? "#4ade80" : "#f87171",
    border:     `1px solid ${ok ? "#166534" : "#7f1d1d"}`,
  }),
  warn:    { background: "#1c1208", border: "1px solid #78350f", borderRadius: "6px", padding: "8px 12px", fontSize: "11px", color: "#fcd34d", marginBottom: "10px" },
  ok:      { background: "#052e16", border: "1px solid #166534", borderRadius: "6px", padding: "8px 12px", fontSize: "11px", color: "#4ade80", marginBottom: "10px" },
  btn:     (v) => ({
    padding: "6px 14px", borderRadius: "5px", border: "none", cursor: "pointer",
    fontSize: "12px", fontWeight: 600,
    background: v === "green" ? "#065f46" : v === "blue" ? "#1e40af" : v === "yellow" ? "#78350f" : "#1e2a45",
    color:      v === "green" ? "#6ee7b7" : v === "blue" ? "#93c5fd" : v === "yellow" ? "#fcd34d" : "#94a3b8",
  }),
  kw:      { display: "inline-block", background: "#0f172a", border: "1px solid #1e2a45", borderRadius: "3px",
             padding: "1px 6px", fontSize: "10px", color: "#64748b", margin: "2px" },
  progress:{ background: "#0f172a", borderRadius: "4px", height: "4px", overflow: "hidden", marginTop: "4px" },
  bar:     (pct, color) => ({ height: "100%", width: `${pct}%`, background: color, borderRadius: "4px", transition: "width 0.5s" }),
  log:     { background: "#020617", border: "1px solid #1e2a45", borderRadius: "6px", padding: "10px",
             fontFamily: "monospace", fontSize: "10px", color: "#94a3b8",
             maxHeight: "200px", overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" },
};

// v5.16: per-asset params come from /brain/calibration_status.asset_meta, which
// the backend derives from config.py (LGBM_PARAMS + ASSET_PROFILES +
// VOLATILITY_TIERS). There is deliberately NO hardcoded table here any more.
//
// ⚠ There used to be one, and it silently lied. After the v5.14 asset swap this
// panel still rendered USDJPY/USDCHF/USDCAD — with their old leaves/LR/SL values —
// for assets the engine had stopped trading entirely, while US500/USTEC/XAUUSD
// were absent. Displayed training parameters that no engine reads are worse than
// no panel at all. Do not reintroduce a literal table.

export default function BrainCalibration() {
  const [status,    setStatus]    = useState(null);
  const [running,   setRunning]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [msg,       setMsg]       = useState(null);
  const [showKw,    setShowKw]    = useState(false);
  const [taskLog,   setTaskLog]   = useState([]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 8000); };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await API.get("/brain/calibration_status");
      setStatus(r.data);
    } catch (e) {
      flash("err", `Status fetch failed: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const runCalibration = async () => {
    setRunning(true);
    setResult(null);
    setTaskLog(["⏳ Starting calibration..."]);
    try {
      setTaskLog(l => [...l, "📡 Refreshing Polymarket markets..."]);
      const r = await API.post("/brain/calibrate");
      const d = r.data;

      const logs = ["✅ Calibration complete"];

      // Polymarket
      const poly = d.tasks?.polymarket;
      if (poly?.status === "ok") {
        logs.push(`📡 Polymarket: ${poly.message}`);
        const probs = poly.probs || {};
        logs.push(`   Recession=${Math.round((probs.recession||0)*100)}%  Rate Hike=${Math.round((probs.rate_hike||0)*100)}%  Oil=${Math.round((probs.oil_disruption||0)*100)}%  Conflict=${Math.round((probs.conflict||0)*100)}%`);
      } else {
        logs.push(`❌ Polymarket: ${poly?.message || "failed"}`);
      }

      // Retrain
      const retrain = d.tasks?.retrain || {};
      Object.entries(retrain).forEach(([sym, r]) => {
        if (r.status === "ok")
          logs.push(`🤖 ${sym}: retrained (iter=${r.best_iteration})`);
        else
          logs.push(`❌ ${sym}: ${r.message}`);
      });

      // Horizons
      const hz = d.tasks?.horizons;
      if (hz?.status === "ok") {
        const hzr = hz.results || {};
        Object.entries(hzr).forEach(([sym, v]) => {
          logs.push(`📐 ${sym}: horizon=${v.best_shift}h (acc=${Math.round((v.accuracy||0)*100)}%)`);
        });
      }

      setTaskLog(logs);
      setResult(d);
      flash("ok", "Calibration complete — all 3 tasks done");
      await fetchStatus();
    } catch (e) {
      setTaskLog(l => [...l, `❌ Error: ${e.response?.data?.detail || e.message}`]);
      flash("err", e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const assetParams = status?.asset_meta || {};
  const models = status?.models || {};
  const poly   = status?.polymarket || {};
  const data   = status?.data_freshness || {};

  return (
    <div style={{ padding: "10px", maxWidth: "1000px", margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={s.card}>
        <div style={s.title}>
          🔬 Brain Calibration — Periodic Review
          <button style={{ ...s.btn("green"), marginLeft: "auto" }}
            onClick={runCalibration} disabled={running}>
            {running ? "⏳ Running..." : "▶ Run Full Calibration"}
          </button>
          <button style={s.btn()} onClick={fetchStatus}>↻</button>
        </div>

        <div style={s.warn}>
          ⚠️ These 3 items need periodic review as macro conditions evolve.
          Auto-calibration runs <strong>weekly</strong> in the background.
          Click <strong>Run Full Calibration</strong> to trigger immediately.
        </div>

        {msg && (
          <div style={msg.type === "ok" ? s.ok : s.warn}>
            {msg.type === "ok" ? "✅" : "❌"} {msg.text}
          </div>
        )}
      </div>

      {/* ── Task 1: Polymarket Keywords ── */}
      <div style={s.card}>
        <div style={s.title}>📡 Task 1 — Polymarket Keywords & Market Coverage</div>
        <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "10px" }}>
          Keywords must match current geopolitical/macro events. New crises may need new keywords.
          Auto-refreshes every 15 minutes. Full keyword review runs weekly.
        </div>

        <div style={s.row}>
          <span style={s.label}>Active unresolved markets</span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ ...s.val, color: poly.active_markets > 50 ? "#4ade80" : "#f59e0b" }}>
              {poly.active_markets ?? "—"}
            </span>
            <span style={{ fontSize: "10px", color: "#475569" }}>
              (sports/politics/crypto — only macro-relevant ones are used)
            </span>
          </div>
        </div>
        <div style={s.row}>
          <span style={s.label}>Cache age</span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={s.val}>{poly.cache_age_mins != null ? `${poly.cache_age_mins} min ago` : "—"}</span>
            {poly.is_stale && (
              <span style={{ ...s.badge(false), fontSize: "10px" }}>
                ⚠️ STALE — Fallback 1.0x active
              </span>
            )}
            {!poly.is_stale && poly.cache_age_mins != null && (
              <span style={{ ...s.badge(true), fontSize: "10px" }}>✅ Fresh</span>
            )}
          </div>
        </div>
        <div style={{ ...s.row, marginTop: "8px" }}>
          <button style={s.btn("blue")}
            onClick={async () => {
              try {
                await API.post("/brain/calibrate");
                flash("ok", "✅ Sentiment refreshed");
                await fetchStatus();
              } catch (e) { flash("err", e.message); }
            }}
            disabled={running}>
            🔄 Refresh Sentiment Now
          </button>
          <span style={{ fontSize: "10px", color: "#475569" }}>
            Stale threshold: 120s — auto-reverts to 1.0x multiplier if exceeded
          </span>
        </div>

        <div style={s.section}>Current Keywords</div>
        <button style={{ ...s.btn(), padding: "3px 8px", fontSize: "10px", marginBottom: "8px" }}
          onClick={() => setShowKw(v => !v)}>
          {showKw ? "▲ Hide" : "▼ Show"} keywords
        </button>
        {showKw && poly.keywords && Object.entries(poly.keywords).map(([key, kws]) => (
          <div key={key} style={{ marginBottom: "8px" }}>
            <div style={{ fontSize: "11px", color: "#60a5fa", marginBottom: "4px" }}>
              {key.replace("_", " ").toUpperCase()}
            </div>
            <div>{kws.map(kw => <span key={kw} style={s.kw}>{kw}</span>)}</div>
          </div>
        ))}
      </div>

      {/* ── Task 2: AI Models ── */}
      <div style={s.card}>
        <div style={s.title}>🤖 Task 2 — AI Model Retraining</div>
        <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "10px" }}>
          Models retrain on 15,000 H1 bars from PostgreSQL. Per-asset params are optimized
          for each asset's volatility and signal strength. Historical data updates every 15 min.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px" }}>
          {Object.keys(assetParams).length === 0 ? (
            <div style={{ color: "#475569", fontSize: "11px" }}>Loading asset params…</div>
          ) : null}
          {Object.entries(assetParams).sort(([a], [b]) => a.localeCompare(b)).map(([sym, params]) => {
            const m = models[sym] || {};
            const d_info = data[sym] || {};
            const ok = m.trained && m.best_iteration > 2;
            return (
              <div key={sym} style={{ background: "#080c18", border: `1px solid ${ok ? "#166534" : "#7f1d1d"}`, borderRadius: "6px", padding: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#e2e8f0" }}>{sym}</span>
                  <span style={s.badge(ok)}>{ok ? "OK" : "WEAK"}</span>
                </div>
                <div style={{ fontSize: "10px", color: "#64748b", lineHeight: "1.6" }}>
                   <div>Horizon: <span style={{ color: "#60a5fa" }}>{m.horizon ?? params.horizon}h</span>
                     {" "}<span style={{ color: params.tier === 1 ? "#a78bfa" : "#60a5fa", fontSize: "9px" }}>Tier {params.tier}</span>
                   </div>
                   <div>Best iter: <span style={{ color: ok ? "#4ade80" : "#f59e0b" }}>{m.best_iteration ?? "—"}</span></div>
                   <div>Leaves: {params.leaves} | LR: {params.lr} | min_child: {params.min_child}</div>
                   {/* v5.16: asset_meta returns NUMBERS (sl_atr 2.0), not the old
                       pre-formatted "2.0x" strings, so the x is appended here. */}
                   <div>SL: <span style={{ color: "#f87171" }}>{params.sl_atr ?? "—"}x ATR</span> | Trail: <span style={{ color: "#4ade80" }}>{params.trail_atr ?? "—"}x ATR</span></div>
                   <div>Bars: <span style={{ color: "#94a3b8" }}>{d_info.bars?.toLocaleString() ?? "—"}</span></div>
                   <div style={{ color: "#475569", marginTop: "2px" }}>{params.note}</div>
                 </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Task 3: Prediction Horizons ── */}
      <div style={s.card}>
        <div style={s.title}>📐 Task 3 — Prediction Horizon Calibration</div>
        <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "10px" }}>
          Sweeps horizons 2h–12h per asset, picks the one with best validation accuracy.
          Runs weekly automatically. Current horizons shown below.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: "6px" }}>
          {Object.entries(models).map(([sym, m]) => (
            <div key={sym} style={{ background: "#080c18", border: "1px solid #1e2a45", borderRadius: "6px", padding: "8px", textAlign: "center" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", marginBottom: "4px" }}>{sym}</div>
              <div style={{ fontSize: "20px", fontWeight: 700, color: "#60a5fa" }}>{m.horizon ?? "—"}</div>
              <div style={{ fontSize: "9px", color: "#475569" }}>hours</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Calibration Log ── */}
      {taskLog.length > 0 && (
        <div style={s.card}>
          <div style={{ ...s.title, justifyContent: "space-between" }}>
            <span>📋 Last Calibration Log</span>
            <button style={{ ...s.btn(), padding: "2px 8px", fontSize: "10px" }}
              onClick={() => setTaskLog([])}>✕ Clear</button>
          </div>
          <div style={s.log}>{taskLog.join("\n")}</div>
        </div>
      )}

      {/* ── Data Freshness ── */}
      <div style={s.card}>
        <div style={s.title}>💾 Historical Data Freshness</div>
        {Object.entries(data).map(([sym, d_info]) => {
          const latest = d_info.latest ? new Date(d_info.latest) : null;
          const hoursOld = latest ? (Date.now() - latest.getTime()) / 3600000 : 999;
          const fresh = hoursOld < 24;
          return (
            <div key={sym} style={s.row}>
              <span style={s.label}>{sym}</span>
              <span style={s.val}>{d_info.bars?.toLocaleString()} bars</span>
              <span style={{ fontSize: "11px", color: "#64748b" }}>{d_info.latest}</span>
              <span style={s.badge(fresh)}>{fresh ? "Fresh" : `${Math.round(hoursOld)}h old`}</span>
            </div>
          );
        })}
      </div>

    </div>
  );
}
